import { useQuery } from '@tanstack/react-query'
import * as z from 'zod/mini'
import {
  Atributo,
  Colaborador,
  FichaAluno,
  InicioAluno,
  Instancia,
  ListaAlunos,
  ListaColaboradores,
  Perfil,
  type Permissao,
  Sessao,
  ValoresAtributos,
} from '../../contrato'
import { ErroApi, obter } from './api'

const UMA_HORA = 60 * 60 * 1000
const naoRepetir401 = (n: number, e: unknown) => !(e instanceof ErroApi && e.status === 401) && n < 1

export const useInstancia = () =>
  useQuery({
    queryKey: ['instancia'],
    queryFn: ({ signal }) => obter('/instancia', Instancia, signal),
    staleTime: UMA_HORA,
  })

export const useSessao = () =>
  useQuery({
    queryKey: ['sessao'],
    queryFn: ({ signal }) => obter('/sessao', Sessao, signal),
    staleTime: 5 * 60 * 1000,
    retry: naoRepetir401,
  })

/** A pessoa tem a permissão? Superadmin tem todas. */
export const pode = (s: Sessao | undefined, p: Permissao) => !!s?.permissoes.includes(p)

/** Pode conceder um perfil: só quem tem todas as permissões dele. */
export const podeConceder = (s: Sessao | undefined, perfil: Pick<Perfil, 'permissoes'>) =>
  !!s && (s.superadmin || perfil.permissoes.every((p) => s.permissoes.includes(p)))

export const useInicioAluno = () =>
  useQuery({ queryKey: ['aluno', 'inicio'], queryFn: ({ signal }) => obter('/aluno/inicio', InicioAluno, signal) })

export const useColaboradores = (busca: string, pagina = 1, consulta = '') =>
  useQuery({
    queryKey: ['colaboradores', busca, pagina, consulta],
    queryFn: ({ signal }) =>
      obter(
        `/colaboradores?busca=${encodeURIComponent(busca)}&pagina=${pagina}${consulta}`,
        ListaColaboradores,
        signal,
      ),
    placeholderData: (anterior) => anterior,
  })

export const useColaborador = (id: string) =>
  useQuery({
    queryKey: ['colaborador', id],
    queryFn: ({ signal }) => obter(`/colaboradores/${id}`, Colaborador, signal),
  })

export const usePerfis = () =>
  useQuery({ queryKey: ['perfis'], queryFn: ({ signal }) => obter('/perfis', z.array(Perfil), signal) })

export const useAlunos = (busca: string, pagina: number, consulta: string) =>
  useQuery({
    queryKey: ['alunos', busca, pagina, consulta],
    queryFn: ({ signal }) =>
      obter(`/alunos?busca=${encodeURIComponent(busca)}&pagina=${pagina}${consulta}`, ListaAlunos, signal),
    placeholderData: (anterior) => anterior,
  })

export const useAluno = (id: string) =>
  useQuery({ queryKey: ['aluno', id], queryFn: ({ signal }) => obter(`/alunos/${id}`, FichaAluno, signal) })

export const useAtributos = (nivel: Atributo['nivel'] = 'aluno') =>
  useQuery({
    queryKey: nivel === 'aluno' ? ['atributos'] : ['atributos', nivel],
    queryFn: ({ signal }) => obter(`/atributos?nivel=${nivel}`, z.array(Atributo), signal),
  })

export const useValoresDoAluno = (id: string) =>
  useQuery({
    queryKey: ['atributos-aluno', id],
    queryFn: ({ signal }) => obter(`/alunos/${id}/atributos`, ValoresAtributos, signal),
  })
